/**
 * ICS-202 Draft with AI: open Pratus AI with an editable prompt. Send runs
 * sitrep-style generate into the fillable boxes (bulk or one sparkle source).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { SparklesIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import config from 'constants/apiConfig';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import type { FieldDraftCitation } from 'components/DisasterGPT/fieldDraft/types';
import { usePersistAiFieldProvenance } from 'components/DisasterGPT/fieldDraft/useAiFieldProvenance';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';

import { useIncidentInfo, useUpdateIcsInfo } from '../hooks/incidentInfoHooks';
import { useCreateIncidentObjective } from '../hooks/incidentObjectiveHooks';
import { useRecordFormVersionSave } from '../hooks/icsFormVersionHooks';
import { useICSContext } from '../planningView/ICSContext';
import { useActualIncidentId } from '../hooks/commonHooks';
import { ClickableFieldCitations } from 'components/DisasterGPT/fieldDraft/ClickableFieldCitations';
import { useAiFieldProvenance } from 'components/DisasterGPT/fieldDraft/useAiFieldProvenance';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';

import { Ics202DraftProvider, useIcs202Draft } from './Ics202DraftContext';
import './ics202DraftEdge.css';
import {
  buildIcs202SkillPrompt,
  ICS_202_BASE_ID,
  listIcs202DraftableFields,
  listUscgIcs202DraftFields,
} from './ics202DraftPrompt';

const EMPTY_PENDING_SOURCES: ReadonlySet<string> = new Set();

type DraftObjective = { name: string; description?: string };

const objectiveRows = (after: unknown): DraftObjective[] => {
  if (!Array.isArray(after)) return [];
  return after.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as { name?: unknown; description?: unknown };
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    if (!name) return [];
    const description =
      typeof record.description === 'string' ? record.description : undefined;
    return [{ name, description }];
  });
};
type DraftApiResponse = {
  fields?: Array<{
    field: string;
    after: unknown;
    citations?: FieldDraftCitation[];
  }>;
};

export const Ics202DraftFlow: React.FC<{
  disabled?: boolean;
  children: ReactNode;
}> = ({ disabled = false, children }) => {
  const incidentId = useActualIncidentId();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: incidentInfo } = useIncidentInfo();
  const updateIcsInfo = useUpdateIcsInfo();
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(
    ICS_202_BASE_ID,
    effectivePeriodNumber
  );
  const createObjective = useCreateIncidentObjective();
  const persistProvenance = usePersistAiFieldProvenance();
  const { queueComposerPrompt, registerComposerSendHandler, isVisible } =
    useChatSidebar();

  const fieldMeta = useMemo(() => listIcs202DraftableFields(), []);
  const allowedSources = useMemo(
    () =>
      new Set([
        ...fieldMeta.map((field) => field.source),
        ...listUscgIcs202DraftFields().map((field) => field.source),
      ]),
    [fieldMeta]
  );
  const [generatingSources, setGeneratingSources] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingSources, setPendingSources] = useState<ReadonlySet<string>>(
    EMPTY_PENDING_SOURCES
  );
  const pendingSourcesRef = useRef<string[] | null>(null);

  const currentValues = useCallback(
    (sources: string[]) => {
      const info = (incidentInfo ?? {}) as Record<string, unknown>;
      const values: Record<string, unknown> = {};
      for (const source of sources) {
        values[source] = info[source];
      }
      return values;
    },
    [incidentInfo]
  );

  const generateFromPrompt = useCallback(
    async (prompt: string, sources: string[]) => {
      if (!incidentId || sources.length === 0) return;
      const targeted = new Set(sources);
      setGeneratingSources(targeted);
      try {
        const { data } = await axios.post<DraftApiResponse>(
          config.apiGateway.draftFormFields,
          {
            incidentId,
            formBaseId: ICS_202_BASE_ID,
            prompt,
            sources,
            currentValues: currentValues(sources),
            groupGuid,
          }
        );
        const rows = (data.fields ?? []).filter((row) =>
          targeted.has(row.field)
        );
        const infoRows = rows.filter((row) => row.field !== 'objectives');
        const draftedObjectives = rows.flatMap((row) =>
          row.field === 'objectives' ? objectiveRows(row.after) : []
        );
        if (infoRows.length > 0) {
          await updateIcsInfo.mutateAsync({
            ...((incidentInfo ?? {}) as Record<string, unknown>),
            ...Object.fromEntries(
              infoRows.map((row) => [row.field, row.after])
            ),
          } as Parameters<typeof updateIcsInfo.mutateAsync>[0]);
        }
        for (const objective of draftedObjectives) {
          await createObjective.mutateAsync(objective);
        }
        if (
          !readOnlyForms &&
          (infoRows.length > 0 || draftedObjectives.length > 0)
        ) {
          recordVersion.appendVersion();
        }
        if (rows.length > 0) {
          await persistProvenance({
            incidentId,
            targetKind: 'incident_info',
            targetId: incidentId,
            fields: rows.map((row) => ({
              fieldKey: row.field,
              sources: row.citations ?? [],
            })),
          });
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to generate a draft.'
        );
      } finally {
        setGeneratingSources(new Set());
      }
    },
    [
      incidentId,
      currentValues,
      groupGuid,
      updateIcsInfo,
      incidentInfo,
      readOnlyForms,
      recordVersion,
      persistProvenance,
      createObjective,
    ]
  );

  useEffect(() => {
    registerComposerSendHandler((message) => {
      const sources = pendingSourcesRef.current;
      if (!sources?.length) return false;
      pendingSourcesRef.current = null;
      setPendingSources(EMPTY_PENDING_SOURCES);
      void generateFromPrompt(message, sources);
      return true;
    });
    return () => registerComposerSendHandler(null);
  }, [registerComposerSendHandler, generateFromPrompt]);

  useEffect(() => {
    if (!isVisible) {
      pendingSourcesRef.current = null;
      setPendingSources(EMPTY_PENDING_SOURCES);
    }
  }, [isVisible]);

  const openDraft = useCallback(
    (nextSources?: string[], item?: string) => {
      if (!incidentId) {
        toast.error('Open an incident to draft the ICS 202.');
        return;
      }
      const allow = allowedSources;
      const sources = (
        nextSources?.length
          ? nextSources.filter((source) => allow.has(source))
          : fieldMeta.map((field) => field.source)
      ).filter(Boolean);
      if (sources.length === 0) return;

      const prompt = buildIcs202SkillPrompt(
        nextSources?.length ? sources : undefined
      );
      pendingSourcesRef.current = sources;
      setPendingSources(new Set(sources));
      queueComposerPrompt(prompt, item);
    },
    [incidentId, fieldMeta, allowedSources, queueComposerPrompt]
  );

  const value = useMemo(
    () => ({
      openDraft,
      disabled: disabled || readOnlyForms,
      generatingSources,
      pendingSources,
    }),
    [openDraft, disabled, readOnlyForms, generatingSources, pendingSources]
  );

  return <Ics202DraftProvider value={value}>{children}</Ics202DraftProvider>;
};

/** Steady violet edge around one box while that box is waiting on the composer. */
export const Ics202DraftTarget: React.FC<{
  source: string;
  className?: string;
  children: ReactNode;
}> = ({ source, className, children }) => {
  const draft = useIcs202Draft();
  const pending = !!draft?.pendingSources.has(source);
  return (
    <div className={cn(className, pending && 'ics202-draft-edge')}>
      {children}
    </div>
  );
};

export const Ics202DraftButton: React.FC<{
  label?: string;
  sources?: string[];
}> = ({ label = 'Draft with AI', sources }) => {
  const draft = useIcs202Draft();
  if (!draft) return null;
  const generating = draft.generatingSources.size > 0;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={draft.disabled || generating}
      onClick={() => draft.openDraft(sources, 'ICS-202')}
      data-testid="ics-202-draft-ai"
    >
      <SparklesIcon className={generating ? 'tw:animate-spin' : undefined} />{' '}
      {label}
    </Button>
  );
};

export const Ics202SectionSparkle: React.FC<{
  source: string;
  label: string;
}> = ({ source, label }) => {
  const draft = useIcs202Draft();
  if (!draft || draft.disabled) return null;
  const generating = draft.generatingSources.has(source);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={`Generate ${label}`}
      disabled={draft.generatingSources.size > 0}
      onClick={() => draft.openDraft([source], label.replace(/:\s*$/, ''))}
    >
      <SparklesIcon className={generating ? 'tw:animate-spin' : undefined} />
    </Button>
  );
};

export const Ics202GeneratingBody: React.FC<{
  source: string;
  children: ReactNode;
}> = ({ source, children }) => {
  const draft = useIcs202Draft();
  const incidentId = useActualIncidentId();
  const { data: provenance = [] } = useAiFieldProvenance(
    'incident_info',
    incidentId
  );
  const generating = !!draft?.generatingSources.has(source);
  const row = provenance.find((item) => item.fieldKey === source);
  return (
    <>
      {generating ? <ReportsGeneratingPlaceholder /> : children}
      {row ? <ClickableFieldCitations citations={row.sources ?? []} /> : null}
    </>
  );
};

export default Ics202DraftFlow;
